import { useEffect, useRef, useState } from 'react';

/**
 * AdvancedSvgMarker – places an AdvancedMarkerElement whose visual is provided by raw SVG markup.
 *
 * Props:
 *   map       – google.maps.Map instance
 *   position  – {lat, lng}
 *   svg       – string with <svg>…</svg>
 *   size      – width/height in px (square)
 *   zIndex    – number (optional)
 *   onClick   – callback (optional)
 *   anchor    – anchor point for the marker (optional)
 *   extraClass – additional class(es) for the marker element (optional)
 */
export default function AdvancedSvgMarker({ map, position, svg, size = 24, zIndex = 0, onClick, anchor: _anchor, extraClass = '' }) {
  const markerRef = useRef(null);
  const containerRef = useRef(null);
  const clickRef     = useRef(onClick);

  // Keep the latest onClick without re-adding the listener each render
  useEffect(() => {
    clickRef.current = onClick;
  }, [onClick]);

  /* ---------------------------------------------------------
   * 1) Create the marker ONCE (or when map becomes available).
   *    We do NOT include `svg` or `size` in the deps array –
   *    those are handled in dedicated update effects below so
   *    the marker is not continuously recreated.
   * -------------------------------------------------------*/
  useEffect(() => {
    if (!map || !position || !window.google) return;

    // Lazy-load the marker library if needed
    const ensureLibrary = async () => {
      if (!window.google.maps.marker?.AdvancedMarkerElement) {
        if (typeof window.google.maps.importLibrary === 'function') {
          await window.google.maps.importLibrary('marker');
        }
      }
    };

    (async () => {
      await ensureLibrary();
      if (!window.google.maps.marker?.AdvancedMarkerElement) return;

      const { AdvancedMarkerElement } = window.google.maps.marker;

      const container = document.createElement('div');
      containerRef.current = container;
      container.className = `scalable-marker ${extraClass}`.trim();
      container.style.position = 'relative';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
      container.style.userSelect = 'none';
      container.style.webkitUserSelect = 'none';
      container.style.webkitUserDrag = 'none';

      // Initial content & sizing
      container.innerHTML = svg;
      container.querySelectorAll('svg, img').forEach((node) => {
        node.setAttribute('draggable', 'false');
        node.style.userSelect = 'none';
        node.style.webkitUserSelect = 'none';
        node.style.webkitUserDrag = 'none';
      });
      container.style.width  = `${size}px`;
      container.style.height = `${size}px`;

      const marker = new AdvancedMarkerElement({
        map,
        position,
        content: container,
        zIndex,
      });

      marker.addListener('gmp-click', () => clickRef.current && clickRef.current());

      markerRef.current = marker;
    })();

    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      containerRef.current = null;
    };
  }, [map, /* intentionally omit svg & size */ position?.lat, position?.lng, zIndex, extraClass]);

  /* ---------------------------------------------------------
   * 2) Update SVG & dimensions WITHOUT remounting the marker.
   * -------------------------------------------------------*/
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = svg;
      containerRef.current.style.width  = `${size}px`;
      containerRef.current.style.height = `${size}px`;
      containerRef.current.querySelectorAll('svg, img').forEach((node) => {
        node.setAttribute('draggable', 'false');
        node.style.userSelect = 'none';
        node.style.webkitUserSelect = 'none';
        node.style.webkitUserDrag = 'none';
      });
    }
  }, [svg, size]);

  /* ---------------------------------------------------------
   * 3) Update position smoothly when GPS moves.
   * -------------------------------------------------------*/
  useEffect(() => {
    if (markerRef.current && position) {
      markerRef.current.position = position;
    }
  }, [position?.lat, position?.lng]);

  /* ---------------------------------------------------------
   * 4) Keep zIndex in sync.
   * -------------------------------------------------------*/
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  return null;
}
